import React, {useEffect, useRef, useState} from "react";
import useSWR from "swr";
import {
    AccountingAccountList,
    AccountingAccountRestControllerApiFactory,
    AccountingPeriod,
    AccountingPeriodRestControllerApiFactory,
    AccountingReportRestControllerApiFactory,
    LedgerEntry,
    LedgerReport,
} from "arteco-api-client-ts";
import {
    Autocomplete,
    Box,
    Button,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    TextField,
} from "@mui/material";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import {EmptyState, Page, PageHeader, Panel, SkeletonRows, StatGrid, StatTile, tableContainerSx, tableSx} from "../../../../components/kit";
import {useApiManager} from "../../../../components/utils/api";
import {useNavigate} from "react-router-dom";
import {fmtAmt, fmtAmtNz, fmtAccountCode} from "../../../../components/utils/numbers";
import {dateToString} from "../../../../components/utils/dates";

type LedgerQuery = { account: AccountingAccountList, dateFrom: string, dateTo: string };

const accountLabel = (o: AccountingAccountList) => `${fmtAccountCode((o as any).code)} — ${(o as any).name ?? ""}`;

// Última consulta del extracto en esta sesión: al volver de un asiento se recupera con su cuenta y
// sus fechas, y el extracto sale de la caché al instante mientras se refresca por detrás (#13898).
let lastQuery: LedgerQuery | undefined;

export const AccountLedgerPage = () => {
    const apiManager = useApiManager();
    const navigate = useNavigate();

    const today = new Date();
    const firstOfYear = `${today.getFullYear()}-01-01`;
    const todayStr = today.toISOString().slice(0, 10);

    const [activePeriod, setActivePeriod] = useState<AccountingPeriod | null>(null);
    const [account, setAccount] = useState<AccountingAccountList | null>(lastQuery?.account ?? null);
    const [accountInput, setAccountInput] = useState(lastQuery ? accountLabel(lastQuery.account) : "");
    const [accountOptions, setAccountOptions] = useState<AccountingAccountList[]>(lastQuery ? [lastQuery.account] : []);
    const [dateFrom, setDateFrom] = useState(lastQuery?.dateFrom ?? firstOfYear);
    const [dateTo, setDateTo] = useState(lastQuery?.dateTo ?? todayStr);
    const [query, setQuery] = useState<LedgerQuery | undefined>(lastQuery);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        const periodCtrl = apiManager.factory(AccountingPeriodRestControllerApiFactory);
        apiManager.execute(periodCtrl.currentAccountingPeriod()).then((p) => {
            const period = p as AccountingPeriod;
            setActivePeriod(period);
            // Si se vuelve a una consulta recordada, se respetan sus fechas.
            if (lastQuery) return;
            if (period.startDate) setDateFrom(period.startDate);
            if (period.endDate) setDateTo(period.endDate < todayStr ? period.endDate : todayStr);
        }).catch(() => { /* sin período activo, mantenemos defaults */ });
    }, []);

    const loadRecentAccounts = () => {
        const ctrl = apiManager.factory(AccountingAccountRestControllerApiFactory);
        apiManager
            .execute(ctrl.listAccountingAccount(undefined, undefined, undefined, undefined, undefined, true, undefined, false))
            .then((list) => setAccountOptions(list as any));
    };

    useEffect(() => { loadRecentAccounts(); }, []);

    const handleAccountSearch = (term: string) => {
        if (timerRef.current) clearTimeout(timerRef.current);
        if (!term.trim()) {
            loadRecentAccounts();
            return;
        }
        timerRef.current = setTimeout(() => {
            const ctrl = apiManager.factory(AccountingAccountRestControllerApiFactory);
            apiManager
                .execute(ctrl.listAccountingAccount(undefined, undefined, undefined, undefined, undefined, undefined, term.trim(), false))
                .then((list) => setAccountOptions(list as any));
        }, 250);
    };

    // Caché con revalidación por cuenta y fechas (#13898), como el diario (#13895).
    const {data, isLoading, isValidating, mutate} = useSWR(
        query ? ["ledger.statement", (query.account as any).code, query.dateFrom, query.dateTo] : null,
        () => apiManager.execute(apiManager.factory(AccountingReportRestControllerApiFactory)
            .statement((query!.account as any).code, query!.dateFrom, query!.dateTo))
            .then(r => r as LedgerReport),
        {revalidateOnFocus: false},
    );
    const report: LedgerReport | null = data ?? null;
    const loading = isLoading && !data;

    const handleConsultar = () => {
        if (!account) return;
        const next = {account, dateFrom, dateTo};
        const same = query && (query.account as any).code === (account as any).code
            && query.dateFrom === dateFrom && query.dateTo === dateTo;
        lastQuery = next;
        if (same) mutate(); else setQuery(next);
    };

    const negative = (n?: number) => (n ?? 0) < 0;
    const amountSx = {fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap"} as const;

    return (
        <Page>
            <PageHeader title="Extracto de cuenta"
                        subtitle={activePeriod
                            ? <>Período activo: <strong>{activePeriod.name}</strong>
                                {activePeriod.startDate && activePeriod.endDate && <> ({dateToString(activePeriod.startDate)} – {dateToString(activePeriod.endDate)})</>}.
                                Los asientos de apertura quedan excluidos del extracto.</>
                            : "Movimientos de una cuenta entre dos fechas, con su saldo acumulado."}/>

            <Stack spacing={3}>
                <Panel>
                    <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                        <Autocomplete
                            sx={{width: {xs: "100%", sm: 320}}}
                            value={account}
                            inputValue={accountInput}
                            options={accountOptions}
                            getOptionLabel={accountLabel}
                            isOptionEqualToValue={(a, b) => (a as any).id === (b as any).id}
                            onInputChange={(_, v, reason) => {
                                setAccountInput(v);
                                if (reason === "input") handleAccountSearch(v);
                            }}
                            onChange={(_, v) => {
                                setAccount(v);
                                setQuery(undefined);
                                lastQuery = undefined;
                            }}
                            renderOption={(props, option) => (
                                <li {...props} key={(option as any).id}>
                                    <Box component="span" sx={{fontFamily: "monospace", mr: 1, fontSize: "0.85em", color: "text.secondary"}}>
                                        {fmtAccountCode((option as any).code)}
                                    </Box>
                                    {(option as any).name}
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField {...params} label="Cuenta" size="small" placeholder="Código o nombre…" sx={{width: {xs: "100%", sm: 320}}}/>
                            )}
                            noOptionsText="Sin resultados"
                        />
                        <TextField
                            label="Desde"
                            type="date"
                            size="small"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            InputLabelProps={{shrink: true}}
                            inputProps={{
                                min: activePeriod?.startDate ?? undefined,
                                max: activePeriod?.endDate ?? undefined,
                            }}
                            sx={{width: {xs: "calc(50% - 8px)", sm: 160}}}
                        />
                        <TextField
                            label="Hasta"
                            type="date"
                            size="small"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            InputLabelProps={{shrink: true}}
                            inputProps={{
                                min: activePeriod?.startDate ?? undefined,
                                max: activePeriod?.endDate ?? undefined,
                            }}
                            sx={{width: {xs: "calc(50% - 8px)", sm: 160}}}
                        />
                        <Button
                            variant="contained"
                            sx={{width: {xs: "100%", sm: "auto"}}}
                            onClick={handleConsultar}
                            disabled={!account || isValidating}
                        >
                            {isValidating ? "Cargando…" : "Consultar"}
                        </Button>
                    </Box>
                </Panel>

                {loading && !report && <SkeletonRows rows={6} height={40}/>}

                {!report && !loading && (
                    <Panel>
                        <EmptyState icon={<ReceiptLongRoundedIcon/>} title="Elige una cuenta y pulsa «Consultar»"
                                    description="Se listan sus movimientos en el intervalo de fechas, con el saldo acumulado línea a línea."/>
                    </Panel>
                )}

                {report && (
                    <>
                        <StatGrid>
                            <StatTile label="Debe" value={fmtAmt(report.totalDebit)}/>
                            <StatTile label="Haber" value={fmtAmt(report.totalCredit)}/>
                            <StatTile label="Saldo final" tone={negative(report.finalBalance) ? "error" : "neutral"}
                                      value={<Box component="span" sx={{color: negative(report.finalBalance) ? "error.main" : undefined}}>{fmtAmt(report.finalBalance)}</Box>}/>
                            <StatTile label="Movimientos" value={(report.entries ?? []).length}/>
                        </StatGrid>

                        {(report.subaccounts ?? []).length > 1 && (
                            <Panel title="Subcuentas"
                                   subtitle={`El mayor de la ${report.accountCode} suma ${(report.subaccounts ?? []).length} subcuentas: este es el saldo de cada una.`}>
                                <TableContainer sx={{...tableContainerSx, overflowX: "auto"}}>
                                    <Table size="small" sx={{...tableSx, minWidth: 560}}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Subcuenta</TableCell>
                                                <TableCell align="right">Saldo inicial</TableCell>
                                                <TableCell align="right">Debe</TableCell>
                                                <TableCell align="right">Haber</TableCell>
                                                <TableCell align="right">Saldo final</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(report.subaccounts ?? []).map(sa => (
                                                <TableRow key={sa.accountCode}>
                                                    <TableCell>
                                                        <Box component="span" sx={{fontFamily: "monospace", mr: 1}}>{sa.accountCode}</Box>{sa.accountName}
                                                    </TableCell>
                                                    <TableCell align="right" sx={amountSx}>{fmtAmt(sa.openingBalance)}</TableCell>
                                                    <TableCell align="right" sx={amountSx}>{fmtAmt(sa.totalDebit)}</TableCell>
                                                    <TableCell align="right" sx={amountSx}>{fmtAmt(sa.totalCredit)}</TableCell>
                                                    <TableCell align="right" sx={{...amountSx, fontWeight: 700, color: negative(sa.finalBalance) ? "error.main" : undefined}}>
                                                        {fmtAmt(sa.finalBalance)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Panel>
                        )}

                        <Panel title={<><Box component="span" sx={{fontFamily: "monospace", mr: 1}}>{report.accountCode}</Box>{report.accountName}</>}
                               subtitle={(report.subaccounts ?? []).length > 1
                                   ? "Movimientos de todas sus subcuentas. Pulsa uno para abrir su asiento."
                                   : "Pulsa un movimiento para abrir su asiento."}>
                            {/* En móvil la tabla se desplaza dentro de su caja: el extracto se lee por columnas. */}
                            <TableContainer sx={{...tableContainerSx, overflowX: "auto"}}>
                                <Table size="small" sx={{...tableSx, tableLayout: "fixed", minWidth: 760}}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell width={90}>Asiento</TableCell>
                                            <TableCell width={100}>Fecha</TableCell>
                                            <TableCell>Concepto</TableCell>
                                            <TableCell width={130}>Documento</TableCell>
                                            <TableCell align="right" width={115}>Debe</TableCell>
                                            <TableCell align="right" width={115}>Haber</TableCell>
                                            <TableCell align="right" width={120}>Saldo</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(report.entries ?? []).map((entry: LedgerEntry, idx: number) => (
                                            <TableRow
                                                key={idx}
                                                hover
                                                sx={{cursor: entry.journalEntryId ? "pointer" : "default"}}
                                                onClick={() => {
                                                    if (entry.journalEntryId)
                                                        navigate(`/app/admin/accounting/journal/${entry.journalEntryId}`);
                                                }}
                                            >
                                                <TableCell sx={{fontFamily: "monospace"}}>{entry.entryNumber}</TableCell>
                                                <TableCell sx={amountSx}>{dateToString(entry.entryDate)}</TableCell>
                                                <TableCell sx={{whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}
                                                           title={entry.lineDescription || entry.description}>
                                                    {entry.lineDescription || entry.description}
                                                </TableCell>
                                                <TableCell sx={{fontFamily: "monospace", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "text.secondary"}}>{entry.documentNumber}</TableCell>
                                                <TableCell align="right" sx={amountSx}>{fmtAmtNz(entry.debit)}</TableCell>
                                                <TableCell align="right" sx={amountSx}>{fmtAmtNz(entry.credit)}</TableCell>
                                                <TableCell align="right" sx={{...amountSx, fontWeight: 700, color: negative(entry.runningBalance) ? "error.main" : undefined}}>
                                                    {fmtAmt(entry.runningBalance)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                    <TableFooter sx={{"& td": {fontWeight: 700, color: "text.primary", fontSize: 14, bgcolor: "grey.50"}}}>
                                        <TableRow>
                                            <TableCell colSpan={4}>Totales</TableCell>
                                            <TableCell align="right" sx={amountSx}>{fmtAmt(report.totalDebit)}</TableCell>
                                            <TableCell align="right" sx={amountSx}>{fmtAmt(report.totalCredit)}</TableCell>
                                            <TableCell align="right" sx={{...amountSx, color: negative(report.finalBalance) ? "error.main !important" : undefined}}>
                                                {fmtAmt(report.finalBalance)}
                                            </TableCell>
                                        </TableRow>
                                    </TableFooter>
                                </Table>
                            </TableContainer>
                        </Panel>
                    </>
                )}
            </Stack>
        </Page>
    );
};
